//
// Kicksta (estate-a: same backend platform as Buzzoid and Upleap) — Bearer user search.
//   GET <host>/api/v1/instagram/search?context=user&query=<q>      Authorization: Bearer <token>
// Needs SOCIAL_PROV_KICKSTA_EMAIL / _PASSWORD; without them the router skips the lane.
//

import { parseIgUserSearch, toIgProfile, browserHeaders } from '../../router/shapes';
import type { Provider, TokenAuthConfig } from '../../router/registry';

const HOST = 'https://api.kicksta.co';

export const KICKSTA_TOKEN_AUTH: TokenAuthConfig = {
	loginUrl: `${HOST}/api/v1/oauth/login`,
	loginBody: (email, password) => ({ body: JSON.stringify({ username: email, password }) }),
	// Verified 2026-10-31: login answers { status: 'ok', token } — a 7-day Keycloak JWT, no refresh_token.
	// The router therefore renews by re-login 60 s before `exp`; add a `refresh` block here if the
	// platform ever starts issuing refresh tokens (tokens.ts already honours it).
	parseTokens: (json) => {
		const access = json?.access_token ?? json?.token ?? json?.data?.access_token ?? json?.data?.token;
		if (typeof access !== 'string' || !access) return null;
		return {
			accessToken: access,
			refreshToken: json?.refresh_token ?? json?.data?.refresh_token,
			expiresInS: typeof json?.expires_in === 'number' ? json.expires_in : undefined,
		};
	},
	fallbackTtlS: 6 * 24 * 3600,
};

export const Kicksta: Provider = {
	id: 'kicksta',
	platform: 'instagram',
	capabilities: ['search_users', 'profile'],
	family: 'estate-a',
	transport: 'bearer',
	requiresToken: true,
	seedLatencyMs: 1371, // measured by the coordinator 2026-10-31
	tokenAuth: KICKSTA_TOKEN_AUTH,
	request: (req) => ({
		url: req.capability === 'profile'
			? `${HOST}/api/v1/instagram/usernames/${encodeURIComponent(req.query)}`
			: `${HOST}/api/v1/instagram/search?context=user&query=${encodeURIComponent(req.query)}`,
		init: {
			method: 'GET',
			headers: browserHeaders({ Authorization: `Bearer ${req.token}`, Origin: 'https://kicksta.co' }),
		},
	}),
	parser: (body, req) => (req.capability === 'profile'
		? toIgProfile((body as any)?.data)
		: parseIgUserSearch(body, req.limit)),
};

export default Kicksta;
